export interface DocumentRecommendation {
    document_id: number;
    recommendation: string;
    supporting_quotes: string[];
    document_title: string;
    document_url: string;
    event_id: number;
}   

// Define the schema interface
export interface SchemaItem {
    type: string;  // Changed from answerType to type
    question_definition?: string;
    answer_definition?: string;
    criterion_of_situation?: string;
    criterion_of_action?: string;
    description_value?: string;
    remarks?: string;
    comments?: string;
    answer_options?: string[];  // This replaces the options field
  }

export interface Characteristic {
    answer: string;
    explanation: string;
    confidence: number | null;
    importance: number | null;
    corrected_by?: string;
    comment?: string;
    correction_count?: number;
    isEdited: boolean;
}

/** Coerce API/LLM answer values (incl. numeric 0) to a display/edit string. */
export function normalizeCharacteristicAnswer(answer: unknown): string {
    if (answer === null || answer === undefined) return '';
    if (typeof answer === 'boolean') return answer ? 'Ja' : 'Nee';
    if (Array.isArray(answer)) return answer.join(', ');
    return String(answer);
}

/** True when an answer is present; treats numeric 0 and "0" as valid Getal answers. */
export function hasCharacteristicAnswer(answer: unknown): boolean {
    if (answer === null || answer === undefined) return false;
    if (typeof answer === 'number') return !Number.isNaN(answer);
    if (typeof answer === 'boolean') return true;
    if (Array.isArray(answer)) return answer.length > 0;
    return String(answer).trim() !== '';
}

/** Parse a form string back to the wire format expected by the backend per type. */
export function serializeCharacteristicAnswerForSubmit(
    answer: string,
    schemaType: string | undefined,
): string | number | boolean {
    if (schemaType === 'Getal') {
        if (answer.trim() === '') return 0;
        const n = Number(answer);
        return Number.isNaN(n) ? 0 : n;
    }
    if (schemaType === 'Ja/Nee') {
        if (answer === 'Ja' || answer === 'true' || answer === 'True') return 'Ja';
        if (answer === 'Nee' || answer === 'false' || answer === 'False') return 'Nee';
    }
    return answer;
}

export type Schema = Record<string, SchemaItem>;

export interface Document {
    document_name: string;
    document: {
        id: number;
        document_id: string;
        name: string | null;
        raw_document_presigned_url: string | null;
        file_type?: string | null;
        text_document_presigned_url?: string | null;
    };
    similarity_score: number;
    metadata: {
        text_characteristics: string[];
        non_text_characteristics: string[];
    };
    documentation_retrieved_id: number;
}

export interface Prompt {
    id: number;
    name: string;
    prompt_type: string;
    prompt: string;
}

export interface Phase {
    id: number;
    slug: string;
    description: string | null;
}

export interface Role {
    id: number;
    slug: string;
    description: string | null;
}

export interface Situation {
    id: number;
    name?: string | null;
    gms_id?: string | null;
    archived?: boolean;
    event_log: EventItem[];
}

export interface EventItem {
    id: number;
    order: number | null;
    start_time?: string | null;
    end_time?: string | null;
    event_type: string;
    message_content?: string | null;
}

export interface Address {
    id: number;
    street: string;
    zipcode: string;
    house_number: string;
    data?: any;
}

export type SimulationStatus = 'paused' | 'playing_on_simulation_time' | 'playing_sequentially' | 'finished';

export interface Simulation {
    id: number;
    token: string;
    created_at: string;
    status: SimulationStatus;
    seconds_elapsed: number;
    address: Address;
    situation: number | null;
    free_play_simulation?: boolean;
    event_log?: EventItem[];
    characteristics_estimation_prompt?: Prompt | null;
    ai_operator_prompt?: Prompt | null;
    ai_summary_prompt?: Prompt | null;
    ai_final_recommendation_filter_prompt?: Prompt | null;
    archived?: boolean;
    relevance_threshold?: number;
    allowed_phases?: Phase[];
    allowed_roles?: Role[];
}


export interface EstimatedCharacteristicsReport {
    characteristics: Record<string, {
        answer: string;
        explanation: string;
        confidence: number;
        importance: number;
        corrected_by?: string;
        comment?: string;
        correction_count?: number;
    }>;
    raw_input: string;
    characteristic_event_id: number;
    pager?: PagerSnapshot | null;
    kenmerkenschema?: KenmerkenschemaSnapshot | null;
}

export type PagerUnitStatus =
    | 'Alarm'
    | 'Uitgerukt'
    | 'Terplaatse'
    | 'Ingerukt'
    | 'Terug';

export interface PagerUnit {
    eenheid: string;
    kazerne: string | null;
    eenheid_type: string | null;
    current_status: PagerUnitStatus | null;
    status_history: Partial<Record<PagerUnitStatus, number>>;
}

export interface PagerSnapshot {
    schema_version: number;
    snapshot_time_seconds_elapsed: number;
    address: string | null;
    globale_fase: string | string[] | null;
    grip: string | number | null;
    meldingsclassificatie: {
        raw: string;
        chain: string[];
    } | null;
    type_counts: Record<string, number>;
    units: PagerUnit[];
}

export type KenmerkenschemaDimensionId = 'mens' | 'gebouw' | 'brand' | 'omgeving' | 'interventie';

export interface KenmerkenschemaDimension {
    title: string;
    fact_bullets: string[];
    ai_bullets: string[];
}

export interface KenmerkenschemaSnapshot {
    schema_version: number;
    dimensions: Partial<Record<KenmerkenschemaDimensionId, KenmerkenschemaDimension>>;
}

/** Format simulation elapsed seconds as HH:MM:SS (matches backend _format_seconds_hms). */
export function formatSimulationElapsedSeconds(totalSeconds: number | null | undefined): string {
    const secondsInt = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const hours = Math.floor(secondsInt / 3600);
    const minutes = Math.floor((secondsInt % 3600) / 60);
    const seconds = secondsInt % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/** Normalize REST or WS characteristics_estimation payload into frontend state. */
export function mapCharacteristicsEstimationReport(
    eventId: number,
    estimation: {
        characteristics?: EstimatedCharacteristicsReport['characteristics'];
        raw_input?: string;
        pager?: PagerSnapshot | null;
        kenmerkenschema?: KenmerkenschemaSnapshot | null;
    } | null | undefined,
): EstimatedCharacteristicsReport | null {
    if (!estimation) return null;
    return {
        characteristics: estimation.characteristics || {},
        raw_input: estimation.raw_input || '',
        characteristic_event_id: eventId,
        pager: estimation.pager ?? null,
        kenmerkenschema: estimation.kenmerkenschema ?? null,
    };
}

export interface Event {
    message_sender: string;
    event_type: string;
    message_content: string;
    adjusted_start_time: string;
    other_arguments: Record<string, any>;
    id: number;
}